import { api, CACHE_TIME, getHeaders } from '@/api';
import { validateResponse } from '@/api/utils';
import { queryOptions } from '@tanstack/react-query';

import {
  gridLayoutStateSchema,
  layoutApiResponseSchema,
  saveLayoutResponseSchema,
  type GridLayoutState,
  type LayoutApiResponse,
  type SaveLayoutResponse
} from './schema';

export interface GridLayoutData {
  layoutState: GridLayoutState | null;
  settingId: string | null;
}

// Fetch grid layout for a specific table
const fetchGridLayout = async (code: string): Promise<GridLayoutData> => {
  const response = await api.get<LayoutApiResponse>(`/pms/api/layout/${encodeURIComponent(code)}`, getHeaders());
  const parsed = validateResponse(layoutApiResponseSchema, response.data);

  if (parsed.Error || !parsed.Data) {
    return { settingId: null, layoutState: null };
  }

  const { Id, Data } = parsed.Data;

  if (!Data) {
    return { settingId: Id, layoutState: null };
  }

  try {
    const layoutParsed = gridLayoutStateSchema.safeParse(JSON.parse(Data));
    if (!layoutParsed.success) {
      console.error('Invalid grid layout data:', layoutParsed.error);
      return { settingId: Id, layoutState: null };
    }

    return {
      settingId: Id,
      layoutState: layoutParsed.data
    };
  } catch (error) {
    console.error('Failed to parse grid layout JSON:', error);
    return { settingId: Id, layoutState: null };
  }
};

// Save grid layout for a specific table (backend handles upsert)
export const saveGridLayout = async (code: string, layoutState: GridLayoutState): Promise<string | null> => {
  const payload = {
    Data: JSON.stringify(layoutState)
  };

  const response = await api.post<SaveLayoutResponse>(
    `/pms/api/layout/${encodeURIComponent(code)}`,
    payload,
    getHeaders()
  );
  const parsed = validateResponse(saveLayoutResponseSchema, response.data);

  if (parsed.Error) {
    throw new Error(parsed.ErrorMessage ?? 'Failed to save grid layout');
  }

  return parsed.Data?.Id ?? null;
};

// Query options for grid layout
export const gridLayoutQueries = {
  all: () => ['gridLayout'] as const,
  byCode: (code: string) => ['gridLayout', code] as const,
  detail: (code: string) =>
    queryOptions({
      queryKey: gridLayoutQueries.byCode(code),
      queryFn: () => fetchGridLayout(code),
      staleTime: CACHE_TIME.STALE,
      gcTime: CACHE_TIME.GC
    })
};
